import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import type { AdViewStats } from "@/pages/Quests/ui/QuestsPage"
import type { UserCardProps } from "@/pages/Leaderboard/ui/LeaderboardPage"
import { axiosInstance } from "@/shared/api/base"
import { IUser } from "@/shared/typings/user"
import { useContext } from "react"
import { ToastContext } from "@/app/providers/ToastProvider"
import { useTranslation } from "react-i18next"
import { Referral } from "../typings/referral"
import { BonusInfo } from "../typings/bonus"

export const useUserInfoQuery = () => {
  const { data: user, isLoading } = useQuery({
    queryKey: ["userinfo"],
    queryFn: (): Promise<IUser> => {
      return axiosInstance.get(`${import.meta.env.VITE_API}/data/getUserInfo`)
    },
  })

  return { user, isLoading }
}

export const useDailyBonus = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: ["dailyBonus"],
    mutationFn: async () => {
      return await axiosInstance.post(
        `${import.meta.env.VITE_API}/data/dailyBonus`
      )
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["dailyBonus"],
      })
      await queryClient.invalidateQueries({
        queryKey: ["userinfo"],
      })
    },
  })
}

export const useGetDailyBonusInfo = () => {
  const { data, isError } = useQuery({
    queryKey: ["dailyBonus"],
    queryFn: (): Promise<BonusInfo> => {
      return axiosInstance.get(`${import.meta.env.VITE_API}/data/dailyBonus`)
    },
  })

  return { data, isError }
}

export const useGetEmojis = () => {
  const { data } = useQuery({
    queryKey: ["emojis"],
    queryFn: async () => {
      const response = await axiosInstance.get(
        `${import.meta.env.VITE_API}/data/getEmojis`
      )

      localStorage.setItem(`emojis`, JSON.stringify(response))

      return response
    },
  })

  return { data }
}

export const useGetLeaderboard = (sortOrder: string) => {
  const { data } = useQuery({
    queryKey: ["leaderboard"],
    queryFn: (): Promise<UserCardProps[]> => {
      return axiosInstance.get(
        `${import.meta.env.VITE_API}/data/topOfUsers/balance/${sortOrder}`
      )
    },
  })

  return { data }
}

export const useExchangeToken = () => {
  const queryClient = useQueryClient()
  const showToast = useContext(ToastContext)
  const { t } = useTranslation("Stars")

  return useMutation({
    mutationKey: ["exchange"],
    mutationFn: async (params: { value: number }) => {
      const { value } = params
      const { data } = await axiosInstance.post(
        `${import.meta.env.VITE_API}/data/exchangeBalance`,
        { amount: value }
      )
      return data
    },
    onError: (error: any) => {
      if (error.response?.status === 402) showToast?.(t("noMoney"), 5000)
      else if (error.response?.status === 409)
        showToast?.(t("alreadyBought"), 5000)
      else showToast?.(t("error"), 5000)
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: ["userinfo"] })
      await queryClient.invalidateQueries({ queryKey: ["balances"] })
    },
  })
}

export const useGetReferralInfo = () => {
  const { data } = useQuery({
    queryKey: ["referralInfo"],
    queryFn: (): Promise<Referral> => {
      return axiosInstance.get(
        `${import.meta.env.VITE_API}/data/getReferralInfo`
      )
    },
  })

  return { data }
}

export const useGetUserAd = () => {
  const { data } = useQuery({
    queryKey: ["userAd"],
    queryFn: (): Promise<AdViewStats> => {
      return axiosInstance.get(`${import.meta.env.VITE_API}/data/getUserAd`)
    },
  })

  return { data }
}

export const useAddFriend = () => {
  return useMutation({
    mutationFn: async ({
      friendId,
    }: FriendPayload): Promise<FriendResponse> => {
      const response = await axiosInstance.post(
        `${import.meta.env.VITE_API}/data/friend/add`,
        { friendId }
      )
      return response.data
    },
  })
}

export const useAcceptFriend = () => {
  return useMutation({
    mutationFn: async ({
      friendId,
    }: FriendPayload): Promise<FriendResponse> => {
      const response = await axiosInstance.post(
        `${import.meta.env.VITE_API}/data/friend/accept`,
        { friendId }
      )
      return response.data
    },
  })
}

export const useCheckFriendStatus = ({ friendId }: { friendId: number }) => {
  return useQuery({
    queryKey: ["friendStatus", friendId],
    queryFn: async (): Promise<FriendStatusResponse> => {
      try {
        const response = await axiosInstance.get(
          `${import.meta.env.VITE_API}/data/friend/status/${friendId}`
        )
        return response.data
      } catch (error) {
        console.error("Error checking friend status:", error)
        return { isFriend: false, isAccepted: false }
      }
    },
    enabled: !!friendId,
  })
}
